/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXEC_CONFIRM: 'execConfirm',
  EXECUTE: 'execute',
  SUCCESS: 'success',
  ERROR: 'error',
  FAILED: 'failed'
};

export default {
  name: 'requirement122',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      displayInfo: '',
      timer: null,
      elapsedTime: 0,
      waitTime: 0,
      interval: 1000,
      isNonDefaultSessionSucceed: true,
      modal_requirement_122: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.START:
        case PHASE.EXEC_CONFIRM:
        case PHASE.EXECUTE:
          return 'secondary';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.ERROR:
        case PHASE.FAILED:
        default:
          return 'danger';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_122 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_122 = false;
    },

    clickOk() {
      this.phase = PHASE.EXEC_CONFIRM;
    },

    /** ルーチン実行処理(NonDefaultSession含む) */
    async execute() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        // response が存在する場合、正常判定を行う(存在しない場合は共通エラー)
        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.waitTime = response.data.user_data.wait_time;
              await this.showExecute();
              break;
            case config.STATUS_WSP_FAILED:
              // NonDefaultSession遷移で異常終了の場合、DefaultSession遷移を行うフラグをfalseにする
              if (response.data.async_code === error.WSP_INITIALIZE_FAILED) {
                this.isNonDefaultSessionSucceed = false;
              }
              this.phase = PHASE.ERROR;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    async showExecute() {
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_122_TXT_3`).replace('$TIME$', 0);
      this.phase = PHASE.EXECUTE;
      this.timer = setInterval(() => {
        if (this.elapsedTime === this.waitTime) {
          clearInterval(this.timer);
          this.stop();
        }
        this.elapsedTime += this.interval;
        this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_122_TXT_3`).replace('$TIME$', Math.ceil(this.elapsedTime / 1000));
      }, this.interval);
    },

    /** ルーチン停止処理(データ読出し、DefaultSession含む) */
    async stop() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'stop'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        // response が存在する場合、正常判定を行う(存在しない場合は共通エラー)
        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
              this.phase = PHASE.ERROR;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    async closeError() {
      if (this.isNonDefaultSessionSucceed) {
        await this.exit();
      } else {
        this.closeWizard();
      }
    },

    /** Exit処理(機能終了) */
    async exit() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        // GET結果に関わらず機能を終了
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result handler.validate で返ってきたバリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },

  mounted() {
    this.init();
  },
};